import { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { fetchSignInMethodsForEmail, type AuthCredential } from 'firebase/auth';
import { doc, getDoc } from 'firebase/firestore';
import { motion, AnimatePresence } from 'framer-motion';
import AnimatedPage from '../components/shared/AnimatedPage';
import { useAuthStore } from '../stores/authStore';
import {
  signInWithGoogle,
  signInWithApple,
  signInWithEmail,
  signUpWithEmail,
  signInWithCustomToken,
  signInAnonymously,
  linkAccount,
  loadProfiles,
} from '../hooks/useAuth';
import { auth, db } from '../lib/firebase';
import { COLLECTIONS } from '../lib/collections';
import { createProfile, checkEmailAvailability, clearPendingSignUp, sendPasswordResetEmail, resolveUnder13Login, initiateParentConsent } from '../lib/functions';
import type { Profile } from '../types/user';

type Mode = 'signin' | 'signup' | 'under13';

const currentYear = new Date().getFullYear();

const YEAR_LABELS = [...Array.from({ length: 12 }, (_, i) => String(currentYear - 6 - i)), '18 and above'];
const YEAR_NUMS   = [...Array.from({ length: 12 }, (_, i) => currentYear - 6 - i), currentYear - 19];
const ADULT_IDX   = YEAR_LABELS.length - 1;
const MONTH_LABELS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
const DRUM_H = 48;

function RolodexDrum({
  items,
  selectedIndex,
  onSelect,
  dark = false,
}: {
  items: string[];
  selectedIndex: number;
  onSelect: (i: number) => void;
  dark?: boolean;
}) {
  const ref = useRef<HTMLDivElement>(null);
  const userScrolling = useRef(false);
  const snapTimer = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
  const fadeBg = dark ? '#0D0F1C' : 'white';

  useEffect(() => {
    if (userScrolling.current) return;
    ref.current?.scrollTo({ top: selectedIndex * DRUM_H, behavior: 'smooth' });
  }, [selectedIndex]);

  function handleScroll() {
    userScrolling.current = true;
    clearTimeout(snapTimer.current);
    snapTimer.current = setTimeout(() => {
      const el = ref.current;
      if (!el) return;
      const idx = Math.max(0, Math.min(items.length - 1, Math.round(el.scrollTop / DRUM_H)));
      el.scrollTo({ top: idx * DRUM_H, behavior: 'smooth' });
      onSelect(idx);
      setTimeout(() => { userScrolling.current = false; }, 200);
    }, 100);
  }

  return (
    <div className="relative select-none flex-1" style={{ height: DRUM_H * 5 }}>
      <div
        className="pointer-events-none absolute inset-x-1 z-10 rounded-xl"
        style={{
          top: DRUM_H * 2,
          height: DRUM_H,
          background: dark ? 'rgba(245,166,35,0.15)' : 'rgba(255,251,235,1)',
          border: dark ? '1px solid rgba(245,166,35,0.5)' : '1px solid rgba(245,166,35,0.4)',
        }}
      />
      <div
        className="pointer-events-none absolute inset-x-0 top-0 z-20"
        style={{ height: DRUM_H * 2, background: `linear-gradient(to bottom, ${fadeBg} 20%, transparent)` }}
      />
      <div
        className="pointer-events-none absolute inset-x-0 bottom-0 z-20"
        style={{ height: DRUM_H * 2, background: `linear-gradient(to top, ${fadeBg} 20%, transparent)` }}
      />
      <div
        ref={ref}
        className="h-full overflow-y-scroll"
        style={{ scrollbarWidth: 'none' }}
        onScroll={handleScroll}
      >
        <div style={{ paddingTop: DRUM_H * 2, paddingBottom: DRUM_H * 2 }}>
          {items.map((label, i) => (
            <div
              key={i}
              style={{ height: DRUM_H }}
              className={`flex cursor-pointer items-center justify-center transition-all duration-100 ${
                i === selectedIndex
                  ? dark ? 'text-base font-semibold text-white' : 'text-base font-semibold text-gray-900'
                  : dark ? 'text-sm text-white/30' : 'text-sm text-gray-400'
              }`}
              onClick={() => {
                onSelect(i);
                ref.current?.scrollTo({ top: i * DRUM_H, behavior: 'smooth' });
              }}
            >
              {label}
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}

function calculateAge(month: number, year: number): number {
  const now = new Date();
  const dob = new Date(year, month, 15);
  let age = now.getFullYear() - dob.getFullYear();
  const monthDiff = now.getMonth() - dob.getMonth();
  if (monthDiff < 0 || (monthDiff === 0 && now.getDate() < 15)) age--;
  return age;
}

function SpaceIllustration() {
  return (
    <div className="flex justify-center">
      <div className="relative" style={{ width: 288, height: 104 }}>
        {/* Static background: stars, planet, sparkles, comet */}
        <svg viewBox="0 0 300 104" width="288" height="104" xmlns="http://www.w3.org/2000/svg" className="absolute inset-0">
          {/* Stars */}
          <circle cx="18" cy="18" r="1.5" fill="#3DFFA0" opacity="0.85" className="animate-pulse" />
          <circle cx="46" cy="7" r="1" fill="#F5A623" opacity="0.9" />
          <circle cx="72" cy="28" r="1.5" fill="#7B61FF" opacity="0.7" className="animate-pulse" />
          <circle cx="148" cy="10" r="1" fill="#3DFFA0" opacity="0.6" />
          <circle cx="202" cy="6" r="1.5" fill="#F5A623" opacity="0.8" className="animate-pulse" />
          <circle cx="228" cy="92" r="1" fill="#3DFFA0" opacity="0.5" />
          <circle cx="280" cy="18" r="1.5" fill="#7B61FF" opacity="0.7" />
          <circle cx="296" cy="62" r="1" fill="#F5A623" opacity="0.6" className="animate-pulse" />
          <circle cx="58" cy="82" r="1" fill="#7B61FF" opacity="0.5" />
          <circle cx="164" cy="88" r="1.5" fill="#F5A623" opacity="0.4" />
          <circle cx="22" cy="72" r="1" fill="#3DFFA0" opacity="0.4" />
          <circle cx="135" cy="96" r="1" fill="#7B61FF" opacity="0.35" />

          {/* Violet planet with rings */}
          <circle cx="248" cy="52" r="26" fill="#5040BB" />
          <circle cx="239" cy="42" r="10" fill="#7B61FF" opacity="0.4" />
          <ellipse cx="248" cy="52" rx="40" ry="9" stroke="#A79AFF" strokeWidth="2.5" opacity="0.5" transform="rotate(-12 248 52)" />
          <ellipse cx="248" cy="52" rx="44" ry="10" stroke="#7B61FF" strokeWidth="1" opacity="0.25" transform="rotate(-12 248 52)" />
          <circle cx="258" cy="60" r="5" fill="#7B61FF" opacity="0.3" />

          {/* Sparkle — amber, left */}
          <path d="M 36 52 L 37.5 57 L 43 58.5 L 37.5 60 L 36 65 L 34.5 60 L 29 58.5 L 34.5 57 Z" fill="#F5A623" opacity="0.75" className="animate-pulse" />

          {/* Sparkle — mint, upper-mid */}
          <path d="M 172 22 L 173.2 26 L 177 27.2 L 173.2 28.4 L 172 32 L 170.8 28.4 L 167 27.2 L 170.8 26 Z" fill="#3DFFA0" opacity="0.65" />

          {/* Sparkle — violet, bottom-mid */}
          <path d="M 178 88 L 179 91 L 182 92 L 179 93 L 178 96 L 177 93 L 174 92 L 177 91 Z" fill="#7B61FF" opacity="0.6" className="animate-pulse" />

          {/* Comet trail */}
          <path d="M 152 80 Q 160 76 170 72" stroke="#F5A623" strokeWidth="1.5" strokeLinecap="round" opacity="0.4" />
          <circle cx="151" cy="81" r="2.5" fill="#F5A623" opacity="0.7" />
        </svg>

        {/* Rocket — animated separately so the bob doesn't move the rest */}
        <div className="absolute inset-0 animate-duo-bounce">
          <svg viewBox="0 0 300 104" width="288" height="104" xmlns="http://www.w3.org/2000/svg">
            {/* Rocket body */}
            <path d="M 98 68 L 98 32 Q 110 13 122 32 L 122 68 Z" fill="#F5A623" />
            {/* Nose cap shading */}
            <path d="M 98 32 Q 110 13 122 32 Q 110 26 98 32" fill="#CC8410" opacity="0.7" />
            {/* Side stripe */}
            <rect x="98" y="48" width="24" height="4" fill="#CC8410" opacity="0.4" rx="1" />
            {/* Window */}
            <circle cx="110" cy="44" r="8.5" fill="#0D0F1C" />
            <circle cx="110" cy="44" r="5.5" fill="#3DFFA0" opacity="0.8" />
            <circle cx="108" cy="42" r="2" fill="white" opacity="0.45" />
            {/* Left fin */}
            <path d="M 98 64 L 83 80 L 98 74 Z" fill="#CC8410" />
            {/* Right fin */}
            <path d="M 122 64 L 137 80 L 122 74 Z" fill="#CC8410" />
            {/* Exhaust nozzle */}
            <rect x="102" y="67" width="16" height="5" rx="2.5" fill="#8A5A08" />
            {/* Flame outer glow */}
            <ellipse cx="110" cy="79" rx="8" ry="11" fill="#F5A623" opacity="0.4" className="animate-flicker" />
            {/* Flame mid */}
            <ellipse cx="110" cy="78" rx="5" ry="8" fill="#3DFFA0" opacity="0.85" className="animate-flicker" style={{ animationDelay: '0.15s' }} />
            {/* Flame core */}
            <ellipse cx="110" cy="76" rx="2.5" ry="4.5" fill="white" opacity="0.7" className="animate-flicker" style={{ animationDelay: '0.3s' }} />
          </svg>
        </div>
      </div>
    </div>
  );
}

// Reusable glass input class
const inputCls = 'mt-1 block w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 text-white placeholder:text-white/30 focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400/40';

// Primary CTA button class
const primaryBtnCls = 'flex min-h-touch w-full items-center justify-center rounded-xl py-3 text-sm font-bold transition-all disabled:opacity-50';

export default function AuthPage() {
  const navigate = useNavigate();
  const location = useLocation();
  const setProfile = useAuthStore((s) => s.setProfile);
  const isExpired = (location.state as { expired?: boolean } | null)?.expired;

  const [mode, setMode] = useState<Mode>(() => {
    const params = new URLSearchParams(window.location.search);
    return params.get('mode') === 'signin' ? 'signin' : 'signup';
  });

  const [dobStep, setDobStep] = useState(() => !sessionStorage.getItem('omore_dob'));
  const [dobMonth, setDobMonth] = useState<number | ''>(() => {
    const stored = sessionStorage.getItem('omore_dob');
    return stored ? new Date(stored).getMonth() : '';
  });
  const [dobYear, setDobYear] = useState<number | ''>(() => {
    const stored = sessionStorage.getItem('omore_dob');
    return stored ? new Date(stored).getFullYear() : '';
  });
  const [yearDrumIdx, setYearDrumIdx] = useState(0);
  const [monthDrumIdx, setMonthDrumIdx] = useState(0);

  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [profiles, setProfiles] = useState<Profile[] | null>(null);
  const [resolving, setResolving] = useState(false);
  const [savedCredential, setSavedCredential] = useState<AuthCredential | null>(null);
  const user = useAuthStore((s) => s.user);

  const [explorerNameInput, setExplorerNameInput] = useState('');
  const [parentEmailInput, setParentEmailInput] = useState('');
  const isUnder13 = dobMonth !== '' && dobYear !== '' && calculateAge(dobMonth as number, dobYear as number) < 13;

  const [signedUpUid, setSignedUpUid] = useState<string | null>(null);

  const [oauthDobStep, setOauthDobStep] = useState(false);
  const [showNameStep, setShowNameStep] = useState(false);
  const [nameStepError, setNameStepError] = useState('');
  const [nameStepLoading, setNameStepLoading] = useState(false);

  const [showPassword, setShowPassword] = useState(false);
  const [showEmailSignIn, setShowEmailSignIn] = useState(false);

  const [forgotPassword, setForgotPassword] = useState(false);
  const [resetEmail, setResetEmail] = useState('');
  const [resetSent, setResetSent] = useState(false);
  const [resetError, setResetError] = useState('');

  const [under13Name, setUnder13Name] = useState('');
  const [under13Password, setUnder13Password] = useState('');
  const [under13Error, setUnder13Error] = useState('');
  const [under13Loading, setUnder13Loading] = useState(false);

  const [under13SignupStep, setUnder13SignupStep] = useState(false);
  const [under13SignupName, setUnder13SignupName] = useState('');
  const [under13SignupEmail, setUnder13SignupEmail] = useState('');
  const [under13SignupError, setUnder13SignupError] = useState('');
  const [under13SignupLoading, setUnder13SignupLoading] = useState(false);
  const [under13DobIso, setUnder13DobIso] = useState('');

  const formFlowActiveRef = useRef(false);

  const [pendingCredential, setPendingCredential] = useState<AuthCredential | null>(null);
  const [, setPendingCredentialEmail] = useState('');
  const [linkMessage, setLinkMessage] = useState('');
  const [pendingExistingProvider, setPendingExistingProvider] = useState('');

  useEffect(() => {
    return () => {
      setPendingCredential(null);
      setPendingCredentialEmail('');
      setLinkMessage('');
      setPendingExistingProvider('');
    };
  }, []);

  function clearPendingLink() {
    setPendingCredential(null);
    setPendingCredentialEmail('');
    setLinkMessage('');
    setPendingExistingProvider('');
  }

  useEffect(() => {
    if (mode === 'signup' && !dobStep && isUnder13) {
      const dobIso = sessionStorage.getItem('omore_dob') || '';
      sessionStorage.removeItem('omore_dob');
      navigate('/age-gate', { replace: true, state: { dob: dobIso, initialStep: 'child-info' } });
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [mode, dobStep, isUnder13]);

  useEffect(() => {
    if (user && !formFlowActiveRef.current && !resolving && !oauthDobStep && !showNameStep) {
      setResolving(true);
      handleAuthSuccess(user.uid)
        .catch((err) => {
          console.error('Auto-resolve auth failed:', err);
          setError(err instanceof Error ? err.message : 'Something went wrong. Please try again.');
        })
        .finally(() => setResolving(false));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [user]);

  async function handleAuthSuccess(uid: string) {
    if (savedCredential) {
      try {
        await linkAccount(savedCredential);
      } catch (err) {
        console.error('linkAccount failed:', err);
      } finally {
        setSavedCredential(null);
      }
    }

    const userProfiles = await loadProfiles(uid);

    if (userProfiles.length === 0) {
      const userDocSnap = await getDoc(doc(db, COLLECTIONS.USERS, uid));
      const firestoreDob = userDocSnap.data()?.pendingDob as string | undefined;
      const firestoreName = userDocSnap.data()?.pendingExplorerName as string | undefined;

      const storedDob = firestoreDob || sessionStorage.getItem('omore_dob') || '';
      const explorerName = firestoreName || sessionStorage.getItem('omore_explorer_name') || '';

      if (storedDob && explorerName) {
        try {
          const result = await createProfile({
            uid,
            explorerName,
            dateOfBirth: storedDob,
            signupPlatform: 'web',
          });
          const newProfiles = await loadProfiles(uid);
          const newProfile = newProfiles.find((p) => p.id === result.data.profileId);
          if (newProfile) {
            setProfile(newProfile);
            clearPendingSignUp({}).catch(() => {});
            sessionStorage.removeItem('omore_dob');
            sessionStorage.removeItem('omore_explorer_name');
            navigate('/onboarding', { replace: true });
            return;
          }
        } catch (err: unknown) {
          const message = err instanceof Error ? err.message : '';
          if (message.includes('already-exists') || message.includes('already taken')) {
            clearPendingSignUp({}).catch(() => {});
            sessionStorage.removeItem('omore_explorer_name');
          } else {
            throw err;
          }
        }
      }

      if (storedDob) {
        setShowNameStep(true);
      } else {
        setOauthDobStep(true);
      }
      return;
    }

    if (userProfiles.length === 1) {
      const profile = userProfiles[0];
      setProfile(profile);
      resolveProfileRedirect(profile);
      return;
    }

    setProfiles(userProfiles);
  }

  function resolveProfileRedirect(profile: Profile) {
    if (profile.subscriptionStatus === 'pending_consent') {
      navigate('/age-gate', { replace: true, state: { consentPending: true } });
      return;
    }
    if (profile.subscriptionStatus === 'rejected') {
      navigate('/age-gate', { replace: true, state: { initialStep: 'rejected' } });
      return;
    }
    if (!profile.hasCompletedSignUp) {
      navigate('/onboarding', { replace: true });
      return;
    }
    if (profile.subscriptionStatus === 'expired') {
      navigate('/auth', { replace: true, state: { expired: true } });
      return;
    }
    const from = (location.state as { from?: { pathname: string } } | null)?.from?.pathname;
    navigate(from || '/spotlight', { replace: true });
  }

  function selectProfile(profile: Profile) {
    setProfile(profile);
    setProfiles(null);
    resolveProfileRedirect(profile);
  }

  function handleDrumContinue() {
    const yr = YEAR_NUMS[yearDrumIdx];
    const mo = yearDrumIdx === ADULT_IDX ? 0 : monthDrumIdx;
    const dobIso = new Date(yr, mo, 1).toISOString();

    if (yearDrumIdx !== ADULT_IDX && calculateAge(mo, yr) < 13) {
      sessionStorage.removeItem('omore_dob');
      setUnder13DobIso(dobIso);
      setDobStep(false);
      setUnder13SignupStep(true);
      return;
    }

    setDobYear(yr);
    setDobMonth(mo);
    sessionStorage.setItem('omore_dob', dobIso);
    setDobStep(false);
  }

  function handleDrumUnder13SignIn() {
    const yr = YEAR_NUMS[yearDrumIdx];
    const mo = monthDrumIdx;
    setDobYear(yr);
    setDobMonth(mo);
    sessionStorage.setItem('omore_dob', new Date(yr, mo, 1).toISOString());
    setDobStep(false);
    setMode('under13');
  }

  async function handleUnder13SignupSubmit(e: React.FormEvent) {
    e.preventDefault();
    setUnder13SignupError('');

    const name = under13SignupName.trim();
    const email = under13SignupEmail.trim();

    if (name.length < 2 || name.length > 30) {
      setUnder13SignupError('Explorer name must be between 2 and 30 characters.');
      return;
    }
    if (!/^[a-zA-Z0-9 ]+$/.test(name)) {
      setUnder13SignupError('Explorer name can only contain letters, numbers, and spaces.');
      return;
    }
    if (!email || !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) {
      setUnder13SignupError('Please enter a valid parent or guardian email address.');
      return;
    }

    setUnder13SignupLoading(true);
    formFlowActiveRef.current = true;
    try {
      const cred = await signInAnonymously();
      const uid = cred.user.uid;

      const { data: profileData } = await createProfile({
        uid,
        explorerName: name,
        dateOfBirth: under13DobIso,
        signupPlatform: 'web',
        subscriptionStatus: 'pending_consent',
        parentEmail: email,
      });

      const { data: consentData } = await initiateParentConsent({
        childDisplayName: name,
        parentEmail: email,
        profileId: profileData.profileId,
        dateOfBirth: under13DobIso,
      });

      sessionStorage.setItem('omore_consent_token', consentData.token);

      navigate('/age-gate', {
        replace: true,
        state: {
          consentPending: true,
          profileId: profileData.profileId,
          token: consentData.token,
        },
      });
    } catch (err: unknown) {
      const fbErr = err as { code?: string; message?: string };
      if (fbErr.code === 'functions/already-exists') {
        setUnder13SignupError('That explorer name is already taken. Please choose another.');
      } else {
        setUnder13SignupError(fbErr.message ?? 'Something went wrong. Please try again.');
      }
    } finally {
      formFlowActiveRef.current = false;
      setUnder13SignupLoading(false);
    }
  }

  function handleOAuthDobContinue() {
    const yr = YEAR_NUMS[yearDrumIdx];
    const mo = yearDrumIdx === ADULT_IDX ? 0 : monthDrumIdx;
    setDobYear(yr);
    setDobMonth(mo);
    sessionStorage.setItem('omore_dob', new Date(yr, mo, 1).toISOString());
    setOauthDobStep(false);
    setShowNameStep(true);
  }

  async function handleOAuthSignIn(provider: 'google' | 'apple') {
    setError('');
    setLoading(true);
    formFlowActiveRef.current = true;
    try {
      const result =
        provider === 'google'
          ? await signInWithGoogle()
          : await signInWithApple();

      if (pendingCredential) {
        await linkAccount(pendingCredential).catch(() => {});
        clearPendingLink();
      }

      const displayName = result.user.displayName ?? '';
      const firstName = displayName.split(' ')[0];
      if (firstName) setExplorerNameInput(firstName);

      await handleAuthSuccess(result.user.uid);
    } catch (err: unknown) {
      const fbErr = err as {
        code?: string;
        credential?: AuthCredential;
        customData?: { email?: string };
      };

      if (fbErr.code === 'auth/account-exists-with-different-credential') {
        const cred = fbErr.credential ?? null;
        const email = fbErr.customData?.email ?? '';

        if (cred && email) {
          try {
            const methods = await fetchSignInMethodsForEmail(auth, email);
            const existingProviderId = methods[0] ?? '';

            if (existingProviderId === 'apple.com' || existingProviderId === 'google.com') {
              setPendingCredential(cred);
              setPendingCredentialEmail(email);
              setPendingExistingProvider(existingProviderId);
              const existingLabel = existingProviderId === 'apple.com' ? 'Apple Sign-In' : 'Google';
              const newLabel = provider === 'google' ? 'Google' : 'Apple Sign-In';
              setLinkMessage(
                `This email is registered with ${existingLabel}. Sign in with ${existingLabel} to link your ${newLabel} account.`,
              );
            } else {
              setError('An account already exists with this email. Try signing in with a different provider to link your accounts.');
            }
          } catch {
            setError('An account already exists with this email. Try signing in with a different provider to link your accounts.');
          }
        } else {
          setError('An account already exists with this email. Try signing in with a different provider to link your accounts.');
        }
      } else {
        setError(err instanceof Error ? err.message : 'Sign-in failed');
      }
    } finally {
      formFlowActiveRef.current = false;
      setLoading(false);
    }
  }

  async function handleEmailSubmit(e: React.FormEvent) {
    e.preventDefault();
    setError('');
    setLoading(true);
    formFlowActiveRef.current = true;
    try {
      if (mode === 'signup') {
        if (!explorerNameInput.trim()) {
          setError('Please enter an explorer name.');
          return;
        }
        if (isUnder13 && !parentEmailInput.trim()) {
          setError("Please enter your parent or guardian's email.");
          return;
        }

        const dobIso = sessionStorage.getItem('omore_dob') || '';

        let uid = signedUpUid;
        if (!uid) {
          try {
            await checkEmailAvailability({ email });
          } catch (preflightErr: unknown) {
            const fbErr = preflightErr as { code?: string };
            if (fbErr.code === 'functions/already-exists') {
              setError('This email is already registered. Try signing in instead.');
            } else {
              setError('Unable to verify email. Please try again.');
            }
            return;
          }
          const result = await signUpWithEmail(email, password);
          uid = result.user.uid;
          setSignedUpUid(uid);
        }

        try {
          const profileResult = await createProfile({
            uid,
            explorerName: explorerNameInput.trim(),
            dateOfBirth: dobIso,
            signupPlatform: 'web',
          });
          const newProfiles = await loadProfiles(uid);
          const newProfile = newProfiles.find((p) => p.id === profileResult.data.profileId);
          if (newProfile) setProfile(newProfile);
        } catch (profileErr: unknown) {
          const fbErr = profileErr as { code?: string };
          if (fbErr.code === 'functions/already-exists') {
            setError(`The name "${explorerNameInput.trim()}" is already taken. Try another!`);
          } else {
            const msg = profileErr instanceof Error ? profileErr.message : 'Something went wrong.';
            setError(msg);
          }
          return;
        }

        await handleAuthSuccess(uid);
        return;
      }

      const result = await signInWithEmail(email, password);
      await handleAuthSuccess(result.user.uid);
    } catch (err: unknown) {
      const code = (err as { code?: string }).code ?? '';
      const message = err instanceof Error ? err.message : 'Authentication failed';
      if (code === 'auth/email-already-in-use' || message.includes('email-already-in-use')) {
        setError('This email is already registered. Try signing in instead.');
      } else if (code === 'auth/invalid-credential' || code === 'auth/wrong-password') {
        setError('Incorrect email or password.');
      } else if (code === 'auth/weak-password') {
        setError('Password must be at least 6 characters.');
      } else {
        setError(message);
      }
    } finally {
      formFlowActiveRef.current = false;
      setLoading(false);
    }
  }

  async function handleForgotPasswordSubmit(e: React.FormEvent) {
    e.preventDefault();
    setResetError('');
    setLoading(true);
    try {
      await sendPasswordResetEmail({ email: resetEmail });
      setResetSent(true);
    } catch (err: unknown) {
      const msg = (err as { code?: string; message?: string }).message ?? '';
      if (msg.includes('google-account')) {
        setResetError("This account uses Google Sign-In. Please sign in with Google instead.");
      } else if (msg.includes('invalid-email')) {
        setResetError("That doesn't look like a valid email address. Double-check and try again.");
      } else if (msg.includes('too-many-requests')) {
        setResetError("Too many attempts — take a breather and try again in a few minutes.");
      } else {
        setResetError("Couldn't send the reset link. Please try again.");
      }
    } finally {
      setLoading(false);
    }
  }

  function openForgotPassword() {
    setResetEmail(email);
    setResetSent(false);
    setResetError('');
    setForgotPassword(true);
  }

  function closeForgotPassword() {
    setForgotPassword(false);
    setResetSent(false);
    setResetError('');
  }

  async function handleNameStepSubmit(e: React.FormEvent) {
    e.preventDefault();
    setNameStepError('');
    if (!explorerNameInput.trim()) {
      setNameStepError('Please enter an explorer name.');
      return;
    }
    if (isUnder13 && !parentEmailInput.trim()) {
      setNameStepError("Please enter your parent or guardian's email.");
      return;
    }

    setNameStepLoading(true);
    const uid = user?.uid ?? '';
    const dobIso = sessionStorage.getItem('omore_dob') || '';
    try {
      if (isUnder13) {
        navigate('/age-gate', {
          state: { dob: dobIso, initialStep: 'child-info', explorerName: explorerNameInput.trim() },
        });
        return;
      }

      try {
        const profileResult = await createProfile({
          uid,
          explorerName: explorerNameInput.trim(),
          dateOfBirth: dobIso,
          signupPlatform: 'web',
        });
        const newProfiles = await loadProfiles(uid);
        const newProfile = newProfiles.find((p) => p.id === profileResult.data.profileId);
        if (newProfile) setProfile(newProfile);
      } catch (profileErr: unknown) {
        const fbErr = profileErr as { code?: string };
        if (fbErr.code === 'functions/already-exists') {
          setNameStepError(`The name "${explorerNameInput.trim()}" is already taken. Try another!`);
        } else {
          setNameStepError(profileErr instanceof Error ? profileErr.message : 'Something went wrong.');
        }
        return;
      }

      await handleAuthSuccess(uid);
    } catch (err) {
      setNameStepError(err instanceof Error ? err.message : 'Something went wrong.');
    } finally {
      setNameStepLoading(false);
    }
  }

  async function handleUnder13Submit(e: React.FormEvent) {
    e.preventDefault();
    setUnder13Error('');
    setUnder13Loading(true);
    try {
      const { data } = await resolveUnder13Login({
        explorerName: under13Name.trim(),
        password: under13Password,
      });
      formFlowActiveRef.current = true;
      await signInWithCustomToken(data.customToken);
      await handleAuthSuccess(auth.currentUser!.uid);
    } catch (err: unknown) {
      const fbErr = err as { code?: string; message?: string };
      console.error('[under13 sign-in]', fbErr.code, fbErr.message);
      if (fbErr.code === 'functions/resource-exhausted') {
        setUnder13Error(fbErr.message ?? 'Too many failed attempts. Please try again later.');
      } else if (fbErr.code === 'functions/not-found' || fbErr.code === 'functions/unauthenticated') {
        setUnder13Error('Explorer name or password is incorrect.');
      } else if (fbErr.code === 'functions/permission-denied') {
        setUnder13Error("Your account isn't active yet. Ask a parent or guardian to check their approval email.");
      } else if (fbErr.code === 'functions/failed-precondition') {
        setUnder13Error("This account doesn't have a password set. Sign in with Apple or Google instead.");
      } else if (fbErr.code === 'functions/invalid-argument') {
        setUnder13Error('Explorer name or password is incorrect.');
      } else {
        setUnder13Error(fbErr.message ?? 'Something went wrong. Please try again.');
      }
    } finally {
      setUnder13Loading(false);
      formFlowActiveRef.current = false;
    }
  }

  // ── Loading state ──
  if (resolving) {
    return (
      <AnimatedPage>
        <div className="flex min-h-screen items-center justify-center" style={{ backgroundColor: '#0D0F1C' }}>
          <div className="h-8 w-8 animate-spin rounded-full border-2 border-amber-400/30 border-t-amber-400" />
        </div>
      </AnimatedPage>
    );
  }

  // ── Profile picker ──
  if (profiles) {
    return (
      <AnimatedPage>
        <div className="flex min-h-screen items-center justify-center px-4" style={{ backgroundColor: '#0D0F1C' }}>
          <div
            className="w-full max-w-md rounded-2xl p-8"
            style={{ background: 'rgba(22,25,41,0.9)', border: '1px solid rgba(255,255,255,0.08)' }}
          >
            <h1 className="text-2xl font-bold text-white">Who's exploring today?</h1>
            <p className="mt-2 text-sm text-white/50">Select your profile to continue.</p>
            <div className="mt-6 space-y-3">
              {profiles.map((profile) => (
                <button
                  key={profile.id}
                  onClick={() => selectProfile(profile)}
                  className="flex w-full min-h-touch items-center gap-4 rounded-xl border border-white/10 bg-white/5 p-4 text-left transition-colors hover:border-amber-400/40 hover:bg-amber-400/5"
                >
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-amber-400/20 text-lg font-bold text-amber-400">
                    {(profile.explorerName || profile.displayName || '?')[0].toUpperCase()}
                  </div>
                  <div>
                    <p className="font-medium text-white">{profile.explorerName || profile.displayName}</p>
                    {profile.subscriptionStatus && (
                      <p className="text-xs text-white/40">{profile.subscriptionStatus}</p>
                    )}
                  </div>
                </button>
              ))}
            </div>
          </div>
        </div>
      </AnimatedPage>
    );
  }

  // ── DOB step (signup or OAuth) ──
  if ((mode === 'signup' && dobStep) || oauthDobStep) {
    return (
      <AnimatedPage>
        <div className="flex min-h-screen items-center justify-center px-4 py-12" style={{ backgroundColor: '#0D0F1C' }}>
          <div
            className="w-full max-w-md rounded-2xl p-8"
            style={{ background: 'rgba(22,25,41,0.9)', border: '1px solid rgba(255,255,255,0.1)', backdropFilter: 'blur(12px)' }}
          >
            <h1 className="text-2xl font-bold text-white">How Old Are You?</h1>
            <p className="mt-2 text-sm text-white/50">
              We ask so we can create the right experience for you.
            </p>

            <div className="mt-6 flex gap-3">
              <AnimatePresence>
                {yearDrumIdx !== ADULT_IDX && (
                  <motion.div
                    key="month-drum"
                    className="flex-1 overflow-hidden"
                    initial={{ opacity: 0, maxWidth: 0 }}
                    animate={{ opacity: 1, maxWidth: 200 }}
                    exit={{ opacity: 0, maxWidth: 0 }}
                    transition={{ duration: 0.25 }}
                  >
                    <p className="mb-2 text-center text-xs font-medium uppercase tracking-wide text-white/40">Month</p>
                    <RolodexDrum dark items={MONTH_LABELS} selectedIndex={monthDrumIdx} onSelect={setMonthDrumIdx} />
                  </motion.div>
                )}
              </AnimatePresence>
              <div className="flex-1">
                <p className="mb-2 text-center text-xs font-medium uppercase tracking-wide text-white/40">Year</p>
                <RolodexDrum dark items={YEAR_LABELS} selectedIndex={yearDrumIdx} onSelect={setYearDrumIdx} />
              </div>
            </div>

            <motion.button
              whileTap={{ scale: 0.97 }}
              onClick={oauthDobStep ? handleOAuthDobContinue : handleDrumContinue}
              className="mt-6 w-full rounded-xl py-3 font-bold text-[#0D0F1C] transition-opacity hover:opacity-90"
              style={{ background: '#F5A623' }}
            >
              Continue
            </motion.button>

            {!oauthDobStep && (
              <p className="mt-6 text-center text-sm text-white/40">
                Already have an account?{' '}
                <button
                  onClick={() => { setMode('signin'); setError(''); }}
                  className="font-semibold text-amber-400 hover:text-amber-300"
                >
                  Sign in
                </button>
              </p>
            )}
            {!oauthDobStep && yearDrumIdx !== ADULT_IDX && calculateAge(monthDrumIdx, YEAR_NUMS[yearDrumIdx]) < 13 && (
              <p className="mt-3 text-center text-sm text-white/35">
                Have an Explorer Name account?{' '}
                <button
                  onClick={handleDrumUnder13SignIn}
                  className="font-semibold text-amber-400 hover:text-amber-300"
                >
                  Sign in with Explorer Name
                </button>
              </p>
            )}
          </div>
        </div>
      </AnimatedPage>
    );
  }

  // ── Main auth page ──
  const showTabs = !pendingCredential && !under13SignupStep && !showNameStep && !forgotPassword;

  return (
    <AnimatedPage>
      <div className="relative min-h-screen" style={{ backgroundColor: '#0D0F1C' }}>
        {/* Background blobs */}
        <div className="pointer-events-none fixed inset-0 overflow-hidden">
          <div
            className="absolute rounded-full"
            style={{
              top: '5%', left: '8%', width: 280, height: 280,
              background: 'radial-gradient(circle, rgba(245,166,35,0.18), transparent 70%)',
              filter: 'blur(40px)',
            }}
          />
          <div
            className="absolute rounded-full"
            style={{
              top: '35%', right: '4%', width: 320, height: 320,
              background: 'radial-gradient(circle, rgba(123,97,255,0.14), transparent 70%)',
              filter: 'blur(50px)',
            }}
          />
          <div
            className="absolute rounded-full"
            style={{
              bottom: '10%', left: '25%', width: 240, height: 240,
              background: 'radial-gradient(circle, rgba(61,255,160,0.12), transparent 70%)',
              filter: 'blur(45px)',
            }}
          />
        </div>

        <div className="relative z-10 flex min-h-screen items-center justify-center px-4 py-12">
          <div className="w-full max-w-md">
            {/* Space illustration */}
            <SpaceIllustration />

            {/* Tab toggle */}
            {showTabs && (
              <div className="mt-4 flex rounded-full p-1" style={{ background: 'rgba(255,255,255,0.06)', border: '1px solid rgba(255,255,255,0.08)' }}>
                <button
                  type="button"
                  onClick={() => { setMode(mode === 'under13' ? 'under13' : 'signin'); setError(''); setForgotPassword(false); }}
                  className={`flex-1 rounded-full py-2 text-sm font-semibold transition-all ${
                    mode === 'signin' || mode === 'under13'
                      ? 'bg-amber-400 text-[#0D0F1C] shadow-sm'
                      : 'text-white/50 hover:text-white'
                  }`}
                >
                  Sign In
                </button>
                <button
                  type="button"
                  onClick={() => { setMode('signup'); setError(''); setForgotPassword(false); setDobStep(!sessionStorage.getItem('omore_dob')); }}
                  className={`flex-1 rounded-full py-2 text-sm font-semibold transition-all ${
                    mode === 'signup'
                      ? 'bg-amber-400 text-[#0D0F1C] shadow-sm'
                      : 'text-white/50 hover:text-white'
                  }`}
                >
                  Sign Up
                </button>
              </div>
            )}

            {/* Glass card */}
            <motion.div
              layout
              className="mt-4 w-full rounded-2xl p-8"
              style={{
                background: 'rgba(22,25,41,0.85)',
                border: '1px solid rgba(255,255,255,0.08)',
                backdropFilter: 'blur(20px)',
              }}
            >
              {pendingCredential ? (
                /* ── Account-linking panel ── */
                <>
                  <h1 className="text-2xl font-bold text-white">Link Your Account</h1>
                  <p className="mt-1 text-sm text-white/50">
                    Sign in with your existing provider to finish linking.
                  </p>

                  <div className="mt-4 rounded-xl border border-amber-400/20 bg-amber-400/10 p-3 text-sm text-amber-300" role="alert">
                    {linkMessage}
                  </div>

                  <div className="mt-6">
                    {pendingExistingProvider === 'apple.com' && (
                      <button
                        onClick={() => handleOAuthSignIn('apple')}
                        disabled={loading}
                        className="flex min-h-touch w-full items-center justify-center gap-3 rounded-xl bg-white py-3 text-sm font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-50"
                        aria-label="Continue with Apple"
                      >
                        <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                          <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                        </svg>
                        Continue with Apple
                      </button>
                    )}
                    {pendingExistingProvider === 'google.com' && (
                      <button
                        onClick={() => handleOAuthSignIn('google')}
                        disabled={loading}
                        className="flex min-h-touch w-full items-center justify-center gap-3 rounded-xl border border-white/15 bg-white/5 py-3 text-sm font-semibold text-white/80 transition-colors hover:bg-white/10 disabled:opacity-50"
                        aria-label="Continue with Google"
                      >
                        <svg className="h-5 w-5" viewBox="0 0 24 24">
                          <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                          <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                          <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                          <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                        </svg>
                        Continue with Google
                      </button>
                    )}
                  </div>

                  <p className="mt-6 text-center text-sm">
                    <button onClick={clearPendingLink} className="font-medium text-amber-400 hover:text-amber-300">
                      &larr; Back to sign in
                    </button>
                  </p>
                </>
              ) : under13SignupStep ? (
                /* ── Under-13 new signup ── */
                <>
                  <button
                    type="button"
                    onClick={() => { setUnder13SignupStep(false); setDobStep(true); setUnder13SignupError(''); }}
                    className="mb-4 flex items-center gap-1 text-sm text-white/40 hover:text-white/70"
                  >
                    &larr; Back
                  </button>
                  <h1 className="text-2xl font-bold text-white">Tell Us About You</h1>
                  <p className="mt-1 text-sm text-white/50">
                    Pick your explorer name, then enter a parent or guardian&apos;s email so we can send them an approval.
                  </p>
                  <form onSubmit={handleUnder13SignupSubmit} className="mt-6 space-y-4">
                    <div>
                      <label htmlFor="u13-signup-name" className="block text-sm font-medium text-white/70">
                        Your Explorer Name
                      </label>
                      <input
                        id="u13-signup-name"
                        type="text"
                        required
                        value={under13SignupName}
                        onChange={(e) => { setUnder13SignupName(e.target.value); if (under13SignupError) setUnder13SignupError(''); }}
                        maxLength={30}
                        placeholder="e.g. StarBuilder"
                        className={inputCls}
                        autoFocus
                      />
                    </div>
                    <div>
                      <label htmlFor="u13-signup-email" className="block text-sm font-medium text-white/70">
                        Parent or Guardian&apos;s Email
                      </label>
                      <p className="mt-0.5 text-xs text-white/35">
                        We&apos;ll send a quick approval to this address — must be a parent or guardian.
                      </p>
                      <input
                        id="u13-signup-email"
                        type="email"
                        required
                        value={under13SignupEmail}
                        onChange={(e) => { setUnder13SignupEmail(e.target.value); if (under13SignupError) setUnder13SignupError(''); }}
                        placeholder="parent@example.com"
                        className={inputCls}
                      />
                    </div>
                    {under13SignupError && (
                      <p className="rounded-xl border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-400" role="alert">
                        {under13SignupError}
                      </p>
                    )}
                    <button
                      type="submit"
                      disabled={under13SignupLoading}
                      className={`${primaryBtnCls} bg-amber-400 text-[#0D0F1C] hover:bg-amber-300`}
                    >
                      {under13SignupLoading ? 'Sending approval email…' : 'Send Approval Email'}
                    </button>
                  </form>
                </>
              ) : showNameStep ? (
                /* ── OAuth new-user name step ── */
                <>
                  <h1 className="text-2xl font-bold text-white">Almost there!</h1>
                  <p className="mt-1 text-sm text-white/50">
                    {isUnder13
                      ? "Pick an explorer name and enter a parent's email for approval."
                      : 'Pick an explorer name for your journey.'}
                  </p>
                  <form onSubmit={handleNameStepSubmit} className="mt-6 space-y-4">
                    <div>
                      <label htmlFor="explorer-name-step" className="block text-sm font-medium text-white/70">
                        Explorer Name
                      </label>
                      <input
                        id="explorer-name-step"
                        type="text"
                        required
                        value={explorerNameInput}
                        onChange={(e) => { setExplorerNameInput(e.target.value); if (nameStepError) setNameStepError(''); }}
                        className={inputCls}
                        placeholder="Your explorer name..."
                        maxLength={30}
                        autoFocus
                      />
                    </div>
                    {isUnder13 && (
                      <div>
                        <label htmlFor="parent-email-step" className="block text-sm font-medium text-white/70">
                          Parent / Guardian&apos;s Email
                        </label>
                        <input
                          id="parent-email-step"
                          type="email"
                          required
                          value={parentEmailInput}
                          onChange={(e) => { setParentEmailInput(e.target.value); if (nameStepError) setNameStepError(''); }}
                          className={inputCls}
                          placeholder="parent@example.com"
                        />
                      </div>
                    )}
                    {nameStepError && (
                      <p className="rounded-xl border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-400" role="alert">
                        {nameStepError}
                      </p>
                    )}
                    <button
                      type="submit"
                      disabled={nameStepLoading}
                      className={`${primaryBtnCls} bg-amber-400 text-[#0D0F1C] hover:bg-amber-300`}
                    >
                      {nameStepLoading ? 'Setting up…' : 'Continue'}
                    </button>
                  </form>
                </>
              ) : forgotPassword ? (
                /* ── Forgot-password panel ── */
                <>
                  <AnimatePresence mode="wait">
                    {resetSent ? (
                      <motion.div
                        key="sent"
                        initial={{ opacity: 0, scale: 0.92 }}
                        animate={{ opacity: 1, scale: 1 }}
                        transition={{ type: 'spring', duration: 0.45 }}
                        className="py-4 text-center"
                      >
                        <motion.div
                          initial={{ scale: 0 }}
                          animate={{ scale: 1 }}
                          transition={{ type: 'spring', delay: 0.1, stiffness: 200 }}
                          className="mx-auto flex h-20 w-20 items-center justify-center rounded-full bg-mint/10 text-4xl"
                        >
                          ✉️
                        </motion.div>
                        <h1 className="mt-5 text-2xl font-extrabold text-white">Check your inbox!</h1>
                        <p className="mt-2 text-sm text-white/50">
                          If <span className="font-medium text-white/80">{resetEmail}</span> is
                          registered, a reset link is on its way. Check spam too!
                        </p>
                        <button
                          onClick={closeForgotPassword}
                          className={`${primaryBtnCls} mt-8 bg-amber-400 text-[#0D0F1C] hover:bg-amber-300`}
                        >
                          Back to sign in
                        </button>
                      </motion.div>
                    ) : (
                      <motion.div
                        key="form"
                        initial={{ opacity: 0, x: 20 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -20 }}
                      >
                        <h1 className="text-2xl font-extrabold text-white">Forgot your password?</h1>
                        <p className="mt-1 text-sm text-white/50">
                          No worries — enter your email and we&apos;ll send a reset link.
                        </p>

                        <form onSubmit={handleForgotPasswordSubmit} className="mt-6 space-y-4">
                          <div>
                            <label htmlFor="reset-email" className="block text-sm font-medium text-white/70">
                              Email address
                            </label>
                            <input
                              id="reset-email"
                              type="email"
                              required
                              value={resetEmail}
                              onChange={(e) => { setResetEmail(e.target.value); if (resetError) setResetError(''); }}
                              className={inputCls}
                              placeholder="you@example.com"
                              autoFocus
                            />
                          </div>

                          <AnimatePresence>
                            {resetError && (
                              <motion.p
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="rounded-xl border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-400"
                                role="alert"
                              >
                                {resetError}
                              </motion.p>
                            )}
                          </AnimatePresence>

                          <button
                            type="submit"
                            disabled={loading}
                            className={`${primaryBtnCls} bg-amber-400 text-[#0D0F1C] hover:bg-amber-300`}
                          >
                            {loading ? (
                              <span className="flex items-center justify-center gap-2">
                                <span className="h-4 w-4 animate-spin rounded-full border-2 border-[#0D0F1C]/30 border-t-[#0D0F1C]" />
                                Sending…
                              </span>
                            ) : (
                              'Send reset link'
                            )}
                          </button>
                        </form>

                        <p className="mt-6 text-center text-sm">
                          <button onClick={closeForgotPassword} className="font-medium text-amber-400 hover:text-amber-300">
                            &larr; Back to sign in
                          </button>
                        </p>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              ) : (
                /* ── Normal auth panel ── */
                <>
                  <h1 className="text-2xl font-bold text-white">
                    {mode === 'signup' ? 'Create Your Account' : mode === 'under13' ? 'Explorer Sign In' : 'Welcome Back'}
                  </h1>
                  <p className="mt-1 text-sm text-white/50">
                    {mode === 'signup'
                      ? 'Start your omore journey today.'
                      : mode === 'under13'
                      ? 'Sign in with your Explorer Name and password.'
                      : 'Sign in to continue your journey.'}
                  </p>

                  {isExpired && (
                    <div className="mt-4 rounded-xl border border-amber-400/20 bg-amber-400/10 p-3 text-sm text-amber-300">
                      Your account access has expired. Please contact support to reactivate.
                    </div>
                  )}

                  {/* OAuth Buttons — not shown for under-13 sign-in form */}
                  {mode !== 'under13' && (
                    <>
                      <div className="mt-6 space-y-3">
                        <button
                          onClick={() => handleOAuthSignIn('apple')}
                          disabled={loading}
                          className="flex min-h-touch w-full items-center justify-center gap-3 rounded-xl bg-white py-3 text-sm font-semibold text-black transition-opacity hover:opacity-90 disabled:opacity-50"
                          aria-label="Continue with Apple"
                        >
                          <svg className="h-5 w-5" viewBox="0 0 24 24" fill="currentColor">
                            <path d="M17.05 20.28c-.98.95-2.05.88-3.08.4-1.09-.5-2.08-.48-3.24 0-1.44.62-2.2.44-3.06-.4C2.79 15.25 3.51 7.59 9.05 7.31c1.35.07 2.29.74 3.08.8 1.18-.24 2.31-.93 3.57-.84 1.51.12 2.65.72 3.4 1.8-3.12 1.87-2.38 5.98.48 7.13-.57 1.5-1.31 2.99-2.54 4.09zM12.03 7.25c-.15-2.23 1.66-4.07 3.74-4.25.29 2.58-2.34 4.5-3.74 4.25z" />
                          </svg>
                          Continue with Apple
                        </button>

                        <button
                          onClick={() => handleOAuthSignIn('google')}
                          disabled={loading}
                          className="flex min-h-touch w-full items-center justify-center gap-3 rounded-xl border border-white/15 bg-white/5 py-3 text-sm font-semibold text-white/80 transition-colors hover:bg-white/10 disabled:opacity-50"
                          aria-label="Continue with Google"
                        >
                          <svg className="h-5 w-5" viewBox="0 0 24 24">
                            <path d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92a5.06 5.06 0 01-2.2 3.32v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.1z" fill="#4285F4" />
                            <path d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z" fill="#34A853" />
                            <path d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z" fill="#FBBC05" />
                            <path d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z" fill="#EA4335" />
                          </svg>
                          Continue with Google
                        </button>
                      </div>

                      <div className="my-5 flex items-center gap-3">
                        <div className="h-px flex-1 bg-white/10" />
                        <span className="text-xs text-white/30">or</span>
                        <div className="h-px flex-1 bg-white/10" />
                      </div>
                    </>
                  )}

                  {/* Under-13 explorer name + password form */}
                  {mode === 'under13' && (
                    <form onSubmit={handleUnder13Submit} className="mt-6 space-y-4">
                      <div>
                        <label htmlFor="u13-name" className="block text-sm font-medium text-white/70">
                          Explorer Name
                        </label>
                        <input
                          id="u13-name"
                          type="text"
                          required
                          value={under13Name}
                          onChange={(e) => { setUnder13Name(e.target.value); if (under13Error) setUnder13Error(''); }}
                          className={inputCls}
                          placeholder="Your explorer name"
                          autoFocus
                        />
                      </div>
                      <div>
                        <label htmlFor="u13-password" className="block text-sm font-medium text-white/70">
                          Password
                        </label>
                        <input
                          id="u13-password"
                          type="password"
                          required
                          value={under13Password}
                          onChange={(e) => { setUnder13Password(e.target.value); if (under13Error) setUnder13Error(''); }}
                          className={inputCls}
                          placeholder="Your password"
                        />
                      </div>
                      {under13Error && (
                        <p className="rounded-xl border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-400" role="alert">
                          {under13Error}
                        </p>
                      )}
                      <button
                        type="submit"
                        disabled={under13Loading}
                        className={`${primaryBtnCls} bg-amber-400 text-[#0D0F1C] hover:bg-amber-300`}
                      >
                        {under13Loading ? 'Signing in…' : 'Sign In with Explorer Name'}
                      </button>
                      <p className="text-center text-sm">
                        <button
                          type="button"
                          onClick={() => { setMode('signin'); setUnder13Error(''); }}
                          className="font-medium text-amber-400 hover:text-amber-300"
                        >
                          &larr; Back to sign in
                        </button>
                      </p>
                    </form>
                  )}

                  {/* Sign-in mode: Explorer Name button + collapsible email form */}
                  {mode === 'signin' && (
                    <div className="space-y-3">
                      <button
                        type="button"
                        onClick={() => { setMode('under13'); setError(''); setShowEmailSignIn(false); }}
                        className="flex min-h-touch w-full items-center justify-center gap-2 rounded-xl border border-amber-400/30 bg-amber-400/10 py-3 text-sm font-semibold text-amber-400 transition-colors hover:bg-amber-400/20"
                      >
                        <svg className="h-4 w-4" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                          <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                          <circle cx="12" cy="7" r="4" />
                        </svg>
                        Sign in with Explorer Name
                      </button>

                      {!showEmailSignIn ? (
                        <p className="text-center text-sm text-white/30">
                          <button
                            type="button"
                            onClick={() => setShowEmailSignIn(true)}
                            className="text-white/40 underline underline-offset-2 hover:text-white/60"
                          >
                            Sign in with email
                          </button>
                        </p>
                      ) : (
                        <form onSubmit={handleEmailSubmit} className="space-y-4">
                          <div>
                            <label htmlFor="email" className="block text-sm font-medium text-white/70">Email</label>
                            <input
                              id="email"
                              type="email"
                              required
                              value={email}
                              onChange={(e) => setEmail(e.target.value)}
                              className={inputCls}
                              placeholder="you@example.com"
                              autoFocus
                            />
                          </div>
                          <div>
                            <label htmlFor="password" className="block text-sm font-medium text-white/70">Password</label>
                            <div className="relative mt-1">
                              <input
                                id="password"
                                type={showPassword ? 'text' : 'password'}
                                required
                                minLength={6}
                                value={password}
                                onChange={(e) => setPassword(e.target.value)}
                                className="block w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 pr-12 text-white placeholder:text-white/30 focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400/40"
                                placeholder="Your password"
                              />
                              <button
                                type="button"
                                onClick={() => setShowPassword((v) => !v)}
                                className="absolute inset-y-0 right-0 flex items-center px-3 text-white/30 hover:text-white/60"
                                aria-label={showPassword ? 'Hide password' : 'Show password'}
                              >
                                {showPassword ? (
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                    <line x1="1" y1="1" x2="23" y2="23" />
                                  </svg>
                                ) : (
                                  <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                    <circle cx="12" cy="12" r="3" />
                                  </svg>
                                )}
                              </button>
                            </div>
                          </div>
                          <AnimatePresence>
                            {error && (
                              <motion.div
                                initial={{ opacity: 0, height: 0 }}
                                animate={{ opacity: 1, height: 'auto' }}
                                exit={{ opacity: 0, height: 0 }}
                                className="rounded-xl border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-400"
                                role="alert"
                              >
                                {error}
                              </motion.div>
                            )}
                          </AnimatePresence>
                          <button
                            type="submit"
                            disabled={loading}
                            className={`${primaryBtnCls} bg-amber-400 text-[#0D0F1C] hover:bg-amber-300`}
                          >
                            {loading ? 'Please wait…' : 'Sign In'}
                          </button>
                          <p className="text-right">
                            <button
                              type="button"
                              onClick={openForgotPassword}
                              className="text-sm text-white/35 hover:text-amber-400"
                            >
                              Forgot password?
                            </button>
                          </p>
                        </form>
                      )}
                    </div>
                  )}

                  {/* Sign-up email form */}
                  {mode === 'signup' && (
                    <form onSubmit={handleEmailSubmit} className="space-y-4">
                      <div>
                        <label htmlFor="explorer-name" className="block text-sm font-medium text-white/70">
                          Explorer Name
                        </label>
                        <input
                          id="explorer-name"
                          type="text"
                          required
                          value={explorerNameInput}
                          onChange={(e) => { setExplorerNameInput(e.target.value); if (error) setError(''); }}
                          className={inputCls}
                          placeholder="Your explorer name..."
                          maxLength={30}
                        />
                      </div>
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-white/70">Email</label>
                        <input
                          id="email"
                          type="email"
                          required
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          className={inputCls}
                          placeholder="you@example.com"
                        />
                      </div>
                      <div>
                        <label htmlFor="password" className="block text-sm font-medium text-white/70">Password</label>
                        <div className="relative mt-1">
                          <input
                            id="password"
                            type={showPassword ? 'text' : 'password'}
                            required
                            minLength={6}
                            value={password}
                            onChange={(e) => setPassword(e.target.value)}
                            className="block w-full rounded-xl border border-white/15 bg-white/5 px-4 py-3 pr-12 text-white placeholder:text-white/30 focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-400/40"
                            placeholder="At least 6 characters"
                          />
                          <button
                            type="button"
                            onClick={() => setShowPassword((v) => !v)}
                            className="absolute inset-y-0 right-0 flex items-center px-3 text-white/30 hover:text-white/60"
                            aria-label={showPassword ? 'Hide password' : 'Show password'}
                          >
                            {showPassword ? (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M17.94 17.94A10.07 10.07 0 0 1 12 20c-7 0-11-8-11-8a18.45 18.45 0 0 1 5.06-5.94M9.9 4.24A9.12 9.12 0 0 1 12 4c7 0 11 8 11 8a18.5 18.5 0 0 1-2.16 3.19m-6.72-1.07a3 3 0 1 1-4.24-4.24" />
                                <line x1="1" y1="1" x2="23" y2="23" />
                              </svg>
                            ) : (
                              <svg xmlns="http://www.w3.org/2000/svg" className="h-5 w-5" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M1 12s4-8 11-8 11 8 11 8-4 8-11 8-11-8-11-8z" />
                                <circle cx="12" cy="12" r="3" />
                              </svg>
                            )}
                          </button>
                        </div>
                      </div>
                      <AnimatePresence>
                        {error && (
                          <motion.div
                            initial={{ opacity: 0, height: 0 }}
                            animate={{ opacity: 1, height: 'auto' }}
                            exit={{ opacity: 0, height: 0 }}
                            className="rounded-xl border border-red-500/20 bg-red-500/10 p-3 text-sm text-red-400"
                            role="alert"
                          >
                            {error}
                          </motion.div>
                        )}
                      </AnimatePresence>
                      <button
                        type="submit"
                        disabled={loading}
                        className={`${primaryBtnCls} bg-amber-400 text-[#0D0F1C] hover:bg-amber-300`}
                      >
                        {loading ? 'Please wait…' : 'Create Account'}
                      </button>
                    </form>
                  )}
                </>
              )}
            </motion.div>
          </div>
        </div>
      </div>
    </AnimatedPage>
  );
}
